/**
 * Geolocalizzazione audience internazionale vs network borghi italiani.
 *
 * - Distanza dal borgo più vicino del network
 * - > 500 km → Tutto il Network, niente filtro prossimità
 * - GPS negato → fallback Campli (mai blocca la UI)
 */

import { distanceMeters } from "./prossimita/proximityEngines";

/** Soglia: oltre 500 km dal borgo più vicino → visitatore internazionale. */
export const INTERNATIONAL_DISTANCE_KM = 500;

/**
 * Ancore GPS del network (borghi di riferimento).
 * Usate per calcolare il borgo più vicino all'utente.
 */
export const NETWORK_BORGO_ANCHORS = [
  { slug: "campli", name: "Campli", lat: 42.7264, lng: 13.6861 },
  { slug: "torricella-sicura", name: "Torricella Sicura", lat: 42.658, lng: 13.652 },
  { slug: "crognaleto", name: "Crognaleto", lat: 42.585, lng: 13.49 },
  { slug: "pietracamela", name: "Pietracamela", lat: 42.523, lng: 13.554 },
  { slug: "mosciano-sant-angelo", name: "Mosciano Sant'Angelo", lat: 42.748, lng: 13.886 },
  { slug: "castelli", name: "Castelli", lat: 42.488, lng: 13.712 },
  { slug: "civitella-del-tronto", name: "Civitella del Tronto", lat: 42.773, lng: 13.666 },
  { slug: "montorio-al-vomano", name: "Montorio al Vomano", lat: 42.58, lng: 13.637 },
  { slug: "rocca-santa-maria", name: "Rocca Santa Maria", lat: 42.69, lng: 13.51 },
  { slug: "castiglione-messer-raimondo", name: "Castiglione Messer Raimondo", lat: 42.515, lng: 13.88 },
  { slug: "assisi", name: "Assisi", lat: 43.0707, lng: 12.6196 },
];

export const INTERNATIONAL_WELCOME_IT =
  "Benvenuto su 6AMICO! Esplora il network dei borghi dall'estero.";

export const INTERNATIONAL_WELCOME_EN =
  "Welcome to 6AMICO! Explore the villages network from abroad.";

/** Messaggio bilingue fisso in cima al feed / colonna centrale. */
export const INTERNATIONAL_WELCOME_BILINGUAL = `${INTERNATIONAL_WELCOME_IT} / ${INTERNATIONAL_WELCOME_EN}`;

/**
 * Borgo del network più vicino alle coordinate utente.
 * @returns {{ slug: string, name: string, lat: number, lng: number, distanceKm: number } | null}
 */
export function nearestNetworkBorgo(lat, lng) {
  if (typeof lat !== "number" || typeof lng !== "number" || Number.isNaN(lat) || Number.isNaN(lng)) {
    return null;
  }

  let best = null;
  let bestM = Infinity;

  for (const borgo of NETWORK_BORGO_ANCHORS) {
    const m = distanceMeters(lat, lng, borgo.lat, borgo.lng);
    if (m < bestM) {
      bestM = m;
      best = {
        slug: borgo.slug,
        name: borgo.name,
        lat: borgo.lat,
        lng: borgo.lng,
        distanceKm: m / 1000,
      };
    }
  }

  return best;
}

/** Distanza (km) dal borgo più vicino; Infinity se coordinate invalide. */
export function distanceToNearestNetworkKm(lat, lng) {
  const nearest = nearestNetworkBorgo(lat, lng);
  return nearest ? nearest.distanceKm : Infinity;
}

/**
 * Classifica l'utente rispetto al network.
 * Criterio principale: distanza > 500 km dal borgo più vicino.
 *
 * @returns {{
 *   international: boolean,
 *   distanceKm: number,
 *   nearestBorgo: { slug: string, name: string, distanceKm: number } | null,
 *   reason: 'local'|'far-from-network'|'invalid',
 *   networkScope: 'local'|'all',
 *   localDistanceFilter: boolean,
 * }}
 */
export function classifyVisitorPosition(lat, lng) {
  const nearest = nearestNetworkBorgo(lat, lng);

  if (!nearest) {
    return {
      international: true,
      distanceKm: Infinity,
      nearestBorgo: null,
      reason: "invalid",
      networkScope: "all",
      localDistanceFilter: false,
    };
  }

  const far = nearest.distanceKm > INTERNATIONAL_DISTANCE_KM;

  if (far) {
    return {
      international: true,
      distanceKm: nearest.distanceKm,
      nearestBorgo: nearest,
      reason: "far-from-network",
      networkScope: "all",
      localDistanceFilter: false,
    };
  }

  return {
    international: false,
    distanceKm: nearest.distanceKm,
    nearestBorgo: nearest,
    reason: "local",
    networkScope: "local",
    localDistanceFilter: true,
  };
}

/** Messaggio di benvenuto internazionale (IT / EN). */
export function internationalWelcomeMessage(_cityName) {
  return INTERNATIONAL_WELCOME_BILINGUAL;
}

/**
 * Reverse geocode best-effort (opzionale, non blocca).
 * @deprecated Preferire INTERNATIONAL_WELCOME_BILINGUAL per il banner.
 */
export async function resolveVisitorCity(lat, lng, { timeoutMs = 2500 } = {}) {
  if (typeof lat !== "number" || typeof lng !== "number") return null;

  const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;

  try {
    const url =
      `https://api.bigdatacloud.net/data/reverse-geocode-client` +
      `?latitude=${encodeURIComponent(lat)}` +
      `&longitude=${encodeURIComponent(lng)}` +
      `&localityLanguage=it`;

    const res = await fetch(url, {
      signal: ctrl?.signal,
      headers: { Accept: "application/json" },
    });
    if (!res.ok) throw new Error(`reverse ${res.status}`);
    const data = await res.json();
    const city =
      data?.city ||
      data?.locality ||
      data?.principalSubdivision ||
      data?.countryName ||
      "";
    return String(city).trim() || null;
  } catch {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** Coordinate di riferimento se GPS fallisce (Campli → Torricella Sicura). */
export function getNetworkFallbackCoords(prefer = "campli") {
  const hit =
    NETWORK_BORGO_ANCHORS.find((b) => b.slug === prefer) ||
    NETWORK_BORGO_ANCHORS.find((b) => b.slug === "torricella-sicura") ||
    NETWORK_BORGO_ANCHORS[0];
  return {
    lat: hit.lat,
    lng: hit.lng,
    slug: hit.slug,
    name: hit.name,
    source: "fallback",
  };
}
